import React, { useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { observer } from 'mobx-react-lite';

import { Comment } from '../../app/entities';
import { CommentItem } from './CommentItem';
import { CommentIcon } from '../icons/CommentIcon';
import { EmptyDrawerPlaceholder } from '../empty-drawer.placeholder';
import { CommentContext } from '../../core/contexts/comment-context/comment.context';
import { DrawingContext } from '@producer/ui-kit';

import styles from './CommentsList.module.scss';
type CommentsListProps = {
  showCompleted?: boolean;
};

export const CommentsList: React.FC<CommentsListProps> = observer(({ showCompleted }) => {
  const {
    playerRef,
    comments,
    selectedComment,
    commentsListRef,
    selectComment,
    setPdfCurrentPage,
  } = useContext(CommentContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const { clearDrawingPath, setIsDrawing } = useContext(DrawingContext);

  const filteredComments = comments.filter((el) =>
    showCompleted ? true : !el.completed,
  ) as Comment[];

  const hasComment = !!comments.length;

  useEffect(() => {
    const currentCommentId = searchParams.get('commentId');

    if (currentCommentId) {
      const comment = Comment.getOne(currentCommentId);

      if (comment) {
        const targetComment = comment?.parent || comment;

        const element = document.getElementById(targetComment._id);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [comments, searchParams, selectedComment]);

  const handleSelectComment = async (comment?: Comment) => {
    if (!comment?._id) {
      selectComment?.(undefined);
    } else {
      const targetComment = comment?.parent || comment;

      if (!targetComment) {
        return;
      }

      selectComment?.(targetComment);

      if (targetComment.timestamp && playerRef?.current) {
        playerRef.current.currentTime = targetComment.timestamp;
        playerRef.current.pause();
      }

      if (targetComment.page || targetComment.page === 0) {
        setPdfCurrentPage?.(targetComment.page);
      }

      if (!targetComment._id || targetComment._id === selectedComment?._id) {
        return;
      }

      clearDrawingPath();
      setIsDrawing(false);

      searchParams.set('commentId', targetComment._id);
      setSearchParams(searchParams);
    }
  };

  return (
    <div className={styles['comments-container']} ref={commentsListRef}>
      {hasComment ? (
        filteredComments?.map((comment) => (
          <div
            className={styles['comment-container']}
            key={comment._id}
            data-selected={comment._id === selectedComment?._id}
          >
            <CommentItem
              comment={comment as Comment}
              onClick={(comment) => {
                if (!comment._id || comment._id === selectedComment?._id) {
                  return;
                }

                handleSelectComment(comment);
              }}
            />

            {comment.replies?.map((reply: Comment) => (
              <CommentItem
                key={reply._id}
                comment={reply}
                onClick={(reply) => {
                  if (!reply.parent._id || reply.parent._id === selectedComment?._id) {
                    return;
                  }

                  handleSelectComment(reply.parent);
                }}
                isReply
              />
            ))}
          </div>
        ))
      ) : (
        <EmptyDrawerPlaceholder
          icon={<CommentIcon />}
          text={'It is quiet in here. Start the conversation by leaving the first comment'}
        />
      )}
    </div>
  );
});
